import { useMemo } from 'react';
import { getSplatUserType, UserProfile } from '@sunrun/sales-experience-shared';
import { usePipeline } from 'providers/Pipeline';
import type { PipelineProspect } from 'providers/Pipeline';
import {
  CHECKOUT,
  CITY_INSPECTION,
  CITY_PERMITTING,
  DESIGN_APPROVAL,
  HOME_REVIEW,
  INSTALL_PREP,
  INSTALLATION,
  POWER_ON,
  ARC,
  NEW_CUSTOMER,
  OFFER_CREATED,
} from 'components/atoms/PipelineConsts';

type UseUrgentTasksResult = {
  urgentTasks: PipelineProspect[];
  isLoading: boolean;
};

const getStageOrder = (
  userProfile: UserProfile | undefined,
  splatUserType: string | undefined,
): string[] => {
  if (!userProfile) return [];

  if (splatUserType === 'CX Pro') {
    return [
      ARC,
      CHECKOUT,
      HOME_REVIEW,
      DESIGN_APPROVAL,
      CITY_PERMITTING,
      INSTALL_PREP,
      INSTALLATION,
      CITY_INSPECTION,
      POWER_ON,
    ];
  }
  if (userProfile.isPartner) {
    return [
      NEW_CUSTOMER,
      OFFER_CREATED,
      CHECKOUT,
      INSTALLATION,
      CITY_INSPECTION,
      POWER_ON,
    ];
  }
  return [
    ARC,
    NEW_CUSTOMER,
    OFFER_CREATED,
    CHECKOUT,
    HOME_REVIEW,
    DESIGN_APPROVAL,
    CITY_PERMITTING,
    INSTALL_PREP,
    INSTALLATION,
    CITY_INSPECTION,
    POWER_ON,
  ];
};

const useUrgentTasks = (
  userProfile: UserProfile | undefined,
): UseUrgentTasksResult => {
  const splatUserType = userProfile ? getSplatUserType(userProfile) : undefined;

  const { data: pipelineData, isLoading } = usePipeline(
    splatUserType,
    userProfile?.contactId ? [userProfile.contactId] : undefined,
  );

  const urgentTasks = useMemo(() => {
    if (!pipelineData?.prospects) {
      return [];
    }

    const allProspects = Object.entries(pipelineData.prospects)
      .flatMap(([, prospects]) => prospects || [])
      .filter((prospect) => prospect.urgentAction) as PipelineProspect[];

    const stageOrder = getStageOrder(userProfile, splatUserType);

    return allProspects.sort((a, b) => {
      const aIsOnHold = a.urgentAction === 'OnHold';
      const bIsOnHold = b.urgentAction === 'OnHold';

      if (aIsOnHold && !bIsOnHold) return 1;
      if (!aIsOnHold && bIsOnHold) return -1;

      const aStageIndex = stageOrder.indexOf(a.pipelineStage);
      const bStageIndex = stageOrder.indexOf(b.pipelineStage);

      return bStageIndex - aStageIndex;
    });
  }, [pipelineData, userProfile, splatUserType]);

  return { urgentTasks, isLoading };
};

export { useUrgentTasks };
export type { UseUrgentTasksResult };
